import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, Chip, Avatar, Skeleton, Collapse,
  useTheme, alpha, IconButton, Tooltip, Button,
} from '@mui/material';
import {
  Refresh, SportsBaseball, LiveTv, CalendarMonth, OpenInNew, TrendingUp,
  CompareArrows,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { teamsApi } from '../services/apiService';
import { getTeamLogoUrl as getSharedTeamLogoUrl } from '../utils/teamLogos';
import MatchupPreview from './MatchupPreview';
import { useYear } from '../context/YearContext';

const getTeamLogoUrl = (tc) => getSharedTeamLogoUrl(tc, 500);

const formatGameTime = (gameTime) => {
  if (!gameTime) return 'TBD';
  try {
    const d = new Date(gameTime);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  } catch { return 'TBD'; }
};

const formatGameDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return dateStr; }
};

/**
 * Horizontal-scrolling upcoming schedule strip for a team.
 * Matches the ScoreboardTicker design from the Dashboard.
 *
 * Props:
 *  - teamId: abbreviated team code (e.g. "HOU")
 *  - teamColor: hex color for accent
 *  - days: how many days ahead to fetch (default 14)
 *  - winProbabilities: optional map of gamePk => { winProbability, factors }
 *  - projections: optional projections object with projectedRecord, confidenceInterval
 */
const UpcomingSchedule = ({ teamId, teamColor = '#1976d2', days = 14, winProbabilities, projections }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { year } = useYear();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null); // for matchup preview
  const hasFetched = useRef(false);

  const fetchUpcoming = useCallback(async () => {
    try {
      if (!hasFetched.current) setLoading(true);
      setError(null);
      const data = await teamsApi.getTeamUpcoming(teamId, { days });
      setGames(data.games || []);
    } catch (err) {
      setError(err.message);
      setGames([]);
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  }, [teamId, days]);

  useEffect(() => {
    fetchUpcoming();
    const interval = setInterval(fetchUpcoming, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, [fetchUpcoming]);

  const getStatus = (game) => {
    if (game.isFinal) return 'Final';
    if (game.isLive) return 'Live';
    const sc = game.statusCode;
    if (sc === 'S' || sc === 'P') return 'Scheduled';
    if (sc === 'I' || sc === 'IR') return 'Live';
    if (sc === 'F' || sc === 'O') return 'Final';
    return 'Scheduled';
  };

  const statusColor = (status) => {
    switch (status) {
      case 'Live': return theme.palette.success.main;
      case 'Final': return theme.palette.text.secondary;
      default: return teamColor;
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Skeleton variant="circular" width={18} height={18} />
            <Skeleton variant="text" width={140} height={20} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'hidden' }}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} variant="rectangular" width={150} height={100} sx={{ borderRadius: 2, flexShrink: 0 }} />
            ))}
          </Box>
        </Box>
      </Card>
    );
  }

  if (error || games.length === 0) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2.5, px: 2, gap: 1, color: 'text.secondary' }}>
          <CalendarMonth fontSize="small" />
          <Typography variant="body2">{error ? 'Failed to load schedule' : 'No upcoming games'}</Typography>
          <IconButton size="small" onClick={fetchUpcoming}><Refresh sx={{ fontSize: 16 }} /></IconButton>
        </Box>
      </Card>
    );
  }

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
      <Box sx={{ p: 2 }}>
        {/* Header Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth sx={{ fontSize: 18, color: teamColor }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
              Upcoming Games
            </Typography>
            <Chip label={games.length} size="small" sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Projected Record Badge */}
            {projections?.projectedRecord && (
              <Tooltip title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.75rem' }}>
                    Projected Record (Log5 Model)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    Based on Pythagorean W%, pitcher matchups, home-field advantage
                  </Typography>
                  {projections.confidenceInterval && (
                    <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem', mt: 0.25 }}>
                      95% CI: {projections.confidenceInterval.low}-{projections.confidenceInterval.high} wins
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                    Avg win prob: {(projections.avgWinProbability * 100).toFixed(1)}%
                  </Typography>
                </Box>
              } arrow>
                <Chip
                  icon={<TrendingUp sx={{ fontSize: 14 }} />}
                  label={`Proj: ${projections.projectedRecord.wins}-${projections.projectedRecord.losses}`}
                  size="small"
                  sx={{
                    height: 24, fontSize: '0.75rem', fontWeight: 700,
                    bgcolor: alpha(teamColor, 0.1), color: teamColor,
                    border: `1px solid ${alpha(teamColor, 0.2)}`,
                    '& .MuiChip-icon': { color: teamColor },
                  }}
                />
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchUpcoming} sx={{ p: 0.5 }}>
                <Refresh sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Horizontal scroll strip */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            py: 0.5,
            px: 0.5,
            mx: -0.5,
            scrollSnapType: 'x proximity',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.2),
              borderRadius: 3,
            },
          }}
        >
          {games.map((game, idx) => {
            const status = getStatus(game);
            const sColor = statusColor(status);
            const wp = winProbabilities && winProbabilities[game.gamePk];
            const wpPct = wp ? Math.round(wp.winProbability * 100) : null;
            const isSelected = selectedGame && selectedGame.gamePk === game.gamePk;

            return (
              <Box key={game.gamePk || idx} sx={{ flexShrink: 0, scrollSnapAlign: 'start' }}>
                <Card
                  elevation={0}
                  sx={{
                    width: 150,
                    cursor: 'pointer',
                    border: `1px solid ${isSelected ? teamColor : alpha(sColor, 0.2)}`,
                    borderRadius: 2,
                    p: 1,
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: alpha(sColor, 0.5),
                      transform: 'translateY(-1px)',
                      boxShadow: theme.shadows[2],
                    },
                    ...(isSelected && {
                      background: alpha(teamColor, 0.06),
                      boxShadow: `0 0 0 1px ${alpha(teamColor, 0.3)}`,
                    }),
                    ...(status === 'Live' && {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.04)}, transparent)`,
                    }),
                  }}
                  onClick={() => {
                    if (status === 'Live' || status === 'Final') {
                      navigate(`/boxscore/${game.gamePk}/${game.date}`);
                    } else if (status === 'Scheduled') {
                      setSelectedGame(isSelected ? null : game);
                    }
                  }}
                >
                  {/* Date + Status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption"
                      sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'text.secondary', lineHeight: 1.2 }}>
                      {formatGameDate(game.date)}
                    </Typography>
                    {status === 'Live' && <LiveTv sx={{ fontSize: 12, color: theme.palette.success.main }} />}
                    {status === 'Scheduled' && (
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {formatGameTime(game.gameTime)}
                      </Typography>
                    )}
                    {status === 'Final' && (
                      <Typography variant="caption"
                        sx={{ fontSize: '0.75rem', fontWeight: 700, color: sColor, textTransform: 'uppercase' }}>
                        Final
                      </Typography>
                    )}
                  </Box>

                  {/* Opponent */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}>
                      {game.homeAway === 'home' ? 'vs' : '@'}
                    </Typography>
                    <Avatar src={getTeamLogoUrl(game.opponent)} sx={{ width: 18, height: 18, fontSize: '0.75rem' }}>
                      {(game.opponent || '?').slice(0, 2)}
                    </Avatar>
                    <Typography variant="body2" fontWeight={700} fontSize="0.8rem" noWrap>
                      {game.opponent || 'TBD'}
                    </Typography>
                  </Box>

                  {/* Score (for live/final) */}
                  {(status === 'Live' || status === 'Final') && game.homeScore != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={700} fontSize="0.85rem">
                        {game.isHome ? game.homeScore : game.awayScore}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>-</Typography>
                      <Typography variant="caption" fontWeight={700} fontSize="0.85rem">
                        {game.isHome ? game.awayScore : game.homeScore}
                      </Typography>
                      {status === 'Live' && game.inning && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: theme.palette.success.main, fontWeight: 600 }}>
                          {game.inning}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Probable Pitcher */}
                  {status === 'Scheduled' && (() => {
                    const opp = game.isHome ? game.probablePitchers?.away : game.probablePitchers?.home;
                    const ours = game.isHome ? game.probablePitchers?.home : game.probablePitchers?.away;
                    return (
                      <Box sx={{ mt: 0.25 }}>
                        {ours && (
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: teamColor, fontWeight: 600, display: 'block', lineHeight: 1.3 }} noWrap>
                            {ours.name?.split(' ').pop()}
                            {ours.era ? ` (${ours.era})` : ''}
                          </Typography>
                        )}
                        {opp && (
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', lineHeight: 1.3 }} noWrap>
                            vs {opp.name?.split(' ').pop()}
                            {opp.era ? ` (${opp.era})` : ''}
                          </Typography>
                        )}
                        {!ours && !opp && (
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled', display: 'block' }}>
                            TBD vs TBD
                          </Typography>
                        )}
                      </Box>
                    );
                  })()}

                  {/* Win Probability Badge */}
                  {wpPct != null && status === 'Scheduled' && (
                    <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{
                        flex: 1, height: 4, borderRadius: 2,
                        bgcolor: alpha(teamColor, 0.1), overflow: 'hidden',
                      }}>
                        <Box sx={{
                          width: `${wpPct}%`, height: '100%', borderRadius: 2,
                          bgcolor: wpPct >= 55 ? theme.palette.success.main
                            : wpPct <= 45 ? theme.palette.error.main
                            : teamColor,
                          transition: 'width 0.4s ease',
                        }} />
                      </Box>
                      <Typography variant="caption" sx={{
                        fontSize: '0.75rem', fontWeight: 800, minWidth: 28, textAlign: 'right',
                        color: wpPct >= 55 ? theme.palette.success.main
                          : wpPct <= 45 ? theme.palette.error.main
                          : teamColor,
                      }}>
                        {wpPct}%
                      </Typography>
                    </Box>
                  )}

                  {/* Matchup hint for scheduled games */}
                  {status === 'Scheduled' && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mt: 0.5, gap: 0.25, opacity: isSelected ? 1 : 0.5,
                      transition: 'opacity 0.15s',
                    }}>
                      <CompareArrows sx={{ fontSize: 10, color: teamColor }} />
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: teamColor, fontWeight: 600 }}>
                        {isSelected ? 'Matchup Open' : 'View Matchup'}
                      </Typography>
                    </Box>
                  )}
                </Card>
              </Box>
            );
          })}
        </Box>

        {/* Expandable Matchup Preview */}
        <Collapse in={!!selectedGame} timeout={300}>
          {selectedGame && (
            <Box sx={{ mt: 1.5 }}>
              <MatchupPreview
                game={selectedGame}
                teamId={teamId}
                teamColor={teamColor}
                season={String(year || '2025')}
                onClose={() => setSelectedGame(null)}
              />
            </Box>
          )}
        </Collapse>
      </Box>
    </Card>
  );
};

export default UpcomingSchedule;
